/**
All constants about how exercises and diet is regulated, which are defined by the CF specs 
should mostly (*) all be declared here, to make sure that they are consistent across all code.

Some constants that are defined by complex rules are exported as lookup functions, so they can be
unit-tested.

(*) It is only "mostly" because some are defined in larger tables like `workout-solver-constants` etc.
*/

import { findLast } from 'lodash-es';
// both type-only, so neither can become a runtime import cycle
import type { MuscleCustomizationGroup } from './data-model/muscle-growth-preference';
import type { MuscleId } from './workout-solver-interface';

/**
 * The id of one body part on the Physique Priority -> Custom screen.
 *
 * It is deliberately the SAME type as `MuscleCustomizationGroup` rather than a
 * new set of ids: a body-part row on that screen is exactly one muscle the user
 * can customize, so the two can never drift apart. The second name exists only
 * so the screen's own code can say what it means — a reader of `BodyPart` should
 * not have to know the solver's word for the same thing.
 */
export type BodyPartId = MuscleCustomizationGroup;

/**
 * One body part row on the Physique Priority -> Custom screen.
 *
 * There is deliberately NO display name here. The English text lives in
 * `client/src/assets/i18n/en.json` and is looked up by id in
 * `client/src/util/muscle-labels.ts`, so it can be translated (#4210 Phase E).
 * Because `id` is a closed union, that lookup table is exhaustive at compile
 * time: adding a body part without adding its label does not build.
 */
export interface BodyPart {
  id: BodyPartId;
}

export const lowerBodyParts: BodyPart[] = [
  { id: 'glutes' },
  { id: 'glutemed' },
  { id: 'quads' },
  { id: 'hams' },
  { id: 'calves' },
];

export const upperBodyParts: BodyPart[] = [
  { id: 'shoulders' },
  { id: 'chest' },
  { id: 'lats' },
  { id: 'traps' },
  { id: 'triceps' },
  { id: 'biceps' },
  { id: 'abs' },
];

// muscle IDs for resulting muscle volume table (from workout solver)
// ordered from bigger to smaller muscle groups
export const lowerBodyResultingMuscleIds: MuscleId[] = ['quads', 'hams', 'glutes', 'glutemed', 'gastrocnemius', 'soleus'];
export const upperBodyResultingMuscleIds: MuscleId[] = ['lats', 'chest', 'biceps', 'triceps', 'rdelt', 'ldelt', 'utraps', 'erectors', 'abs'];

/// The layoff schedule: what a break of a given number of days does to an exercise. One row per
/// step, so the weight cut and the rep cut of a step always share the same day boundary. A
/// schedule change is one edit here, read by both `reducedTargetWeightAfterDays` and
/// `reducedTargetRepsAfterDays`.
///
/// Each `afterDays` is the last gap that gets NO cut from that step, because the comparison
/// below is `days > afterDays`. So the first cut lands at 9 days, the second at 16, and the
/// strength retest at 23.
///
/// #4516: the boundaries used to be 7, 14 and 21, and the first one was a day too tight. A
/// user on a fixed weekly split who moves one session from the Friday to the Saturday leaves a
/// gap of 8 calendar days, and got a 2.5% cut plus a message saying they had been away for over
/// a week. A weekly cadence that slips a single day is not a layoff, so every boundary moved out
/// by one and each step stays exactly a week wide.
type LayoffStep =
  | { afterDays: number; reductionFactor: number; repsOff: number }
  | { afterDays: number; reductionFactor: 'retest' };

const LAYOFF_STEPS: readonly LayoffStep[] = [
  { afterDays: 8, reductionFactor: 0.025, repsOff: 1 },
  { afterDays: 15, reductionFactor: 0.05, repsOff: 2 },
  { afterDays: 22, reductionFactor: 'retest' },
];

/// The layoff step a gap of this many days falls into, or undefined below the first step.
function layoffStepAfterDays(days: number): LayoffStep | undefined {
  return findLast(LAYOFF_STEPS, (step) => days > step.afterDays);
}

/// How much should weight be reduced if the user didn't use that exercise for given number of days.
/// Since #5617 the fraction comes off the one-rep max of the user's best recent set, not off the
/// last lifted weight — see `decideLayoffPrescription` in `client/src/services/layoff-prescription.ts`.
export function reducedTargetWeightAfterDays(days: number): number | 'retest' | undefined {
  return layoffStepAfterDays(days)?.reductionFactor;
}

/** Rep-target companion to `reducedTargetWeightAfterDays`, read off the same `LAYOFF_STEPS` row.
 *  Used by the layoff path when the weight cut would not show on the equipment (e.g. exercises
 *  near the equipment increment floor), or since #5617 when the plan's stored target already
 *  sits at or below the cut weight — see `decideLayoffPrescription` in
 *  `client/src/services/layoff-prescription.ts`. Undefined from 23 days on, because the retest
 *  step carries no rep cut: the layoff path retests before it asks for one. */
export function reducedTargetRepsAfterDays(days: number): number | undefined {
  const step = layoffStepAfterDays(days);
  return step && 'repsOff' in step ? step.repsOff : undefined;
}
